/**
 * useStep5AttackPaths — Step 5 lifecycle.
 *
 * Composes the shared sub-flow hooks into the Step 5 body-props
 * contract:
 *   - `useStructuredFetch` × 3 — attack paths + assets (asset_id
 *     select) + threats (threat_id select)
 *   - `useRecordCRUD`            — add / update / delete
 *   - `useHierarchicalApprove`   — child relevance + approve closure
 *
 * Wire shape: `raw.result.attack_analysis` (outer wrapper has the
 * standard timestamp / agent_name / extraction_method keys). The
 * default `isChild` predicate of `useHierarchicalApprove` handles
 * `source: 'child_tara'`.
 */
import { useCallback } from 'react';
import { getAgentResult } from '../../../api/workflow';
import { useStructuredFetch } from '../hooks/useStructuredFetch';
import { useRefetchOnReviseComplete } from '../hooks/useRefetchOnReviseComplete';
import { useRecordCRUD } from '../hooks/useRecordCRUDv2';
import { useHierarchicalApprove } from '../hooks/useHierarchicalApprove';
import type { Step5BodyProps } from '../step-components/stepBodyProps';
import type {
  PerStepState,
  WorkflowActions,
  WorkflowV2State,
} from '../state/workflowV2Types';
import type { OptionLite } from '../step-components/step5-attack-paths/AttackPathFormModal';

const HAS_OUTPUT = new Set(['draft', 'revising', 'approved']);
const AGENT_NAME = 'attack_path_feasibility_agent';
const ASSET_AGENT_NAME = 'asset_identification_agent';
const THREAT_AGENT_NAME = 'threat_analysis_agent';

interface AgentResultResponse {
  result?: Record<string, unknown>;
  [key: string]: unknown;
}

interface AssetLite {
  asset_id?: string;
  asset_name?: string;
  [key: string]: unknown;
}

interface ThreatLite {
  threat_id?: string;
  threat_scenario?: string;
  stride_category?: string;
  [key: string]: unknown;
}

export interface UseStep5Input {
  threadId: string | null | undefined;
  stepState: PerStepState | undefined;
  workflowCompleted: boolean;
  analysisMode?: string | null;
  /** Reducer state — needed so `useHierarchicalApprove` can read
   *  `state.steps[5].hierarchicalModifications` (Story H2). */
  state: WorkflowV2State;
  /** Action creators bound to the reducer's dispatch. */
  actions: WorkflowActions;
}

export function useStep5AttackPaths({
  threadId,
  stepState,
  workflowCompleted,
  analysisMode,
  state,
  actions,
}: UseStep5Input): Step5BodyProps {
  const status = stepState?.status ?? 'empty';
  const hasOutput = HAS_OUTPUT.has(status);

  /* ----- Attack paths fetch ----- */

  const fetchPaths = useCallback(
    (_signal: AbortSignal): Promise<AgentResultResponse | null> => {
      if (!threadId) return Promise.resolve(null);
      return getAgentResult(threadId, AGENT_NAME) as unknown as Promise<AgentResultResponse>;
    },
    [threadId],
  );

  const extractPaths = useCallback((raw: AgentResultResponse | null): unknown[] => {
    const r = raw?.result as Record<string, unknown> | undefined;
    // Newer agents put records on `attack_analysis`; older variants
    // surfaced them as `attack_paths` or `attack_path_analyses`.
    const list = (r?.attack_analysis ?? r?.attack_paths ?? r?.attack_path_analyses) as
      | unknown[]
      | undefined;
    return Array.isArray(list) ? list : [];
  }, []);

  const structured = useStructuredFetch<AgentResultResponse | null, unknown[]>({
    fetcher: fetchPaths,
    extract: extractPaths,
    enabled: hasOutput && !!threadId,
  });
  useRefetchOnReviseComplete(status, structured.refetch);

  const editable = workflowCompleted || status === 'draft';

  /* ----- Asset list fetch (modal asset_id select) ----- */

  const fetchAssets = useCallback(
    (_signal: AbortSignal): Promise<AgentResultResponse | null> => {
      if (!threadId) return Promise.resolve(null);
      return getAgentResult(threadId, ASSET_AGENT_NAME) as unknown as Promise<AgentResultResponse>;
    },
    [threadId],
  );

  const extractAssets = useCallback((raw: AgentResultResponse | null): AssetLite[] => {
    const r = raw?.result as Record<string, unknown> | undefined;
    const list = (r?.assets ?? r?.identified_assets) as AssetLite[] | undefined;
    return Array.isArray(list) ? list : [];
  }, []);

  const assetsFetch = useStructuredFetch<AgentResultResponse | null, AssetLite[]>({
    fetcher: fetchAssets,
    extract: extractAssets,
    enabled: editable && !!threadId,
  });

  const assetOptions: OptionLite[] = (assetsFetch.data ?? []).map((a) => ({
    value: String(a.asset_id ?? ''),
    label: a.asset_name ? `${a.asset_id} — ${a.asset_name}` : String(a.asset_id ?? ''),
  }));

  /* ----- Threat list fetch (modal threat_id select) -----
   * Step 4 threats can collide on threat_id across child TARAs (no
   * source tag prevents that — different child TARAs each emit
   * THREAT_001 etc.). Dedupe by threat_id so the dropdown stays clean
   * (first label wins, which is typically the current-TARA one). */

  const fetchThreats = useCallback(
    (_signal: AbortSignal): Promise<AgentResultResponse | null> => {
      if (!threadId) return Promise.resolve(null);
      return getAgentResult(threadId, THREAT_AGENT_NAME) as unknown as Promise<AgentResultResponse>;
    },
    [threadId],
  );

  const extractThreats = useCallback((raw: AgentResultResponse | null): ThreatLite[] => {
    const r = raw?.result as Record<string, unknown> | undefined;
    const list = (r?.threats_identified ?? r?.threats) as ThreatLite[] | undefined;
    return Array.isArray(list) ? list : [];
  }, []);

  const threatsFetch = useStructuredFetch<AgentResultResponse | null, ThreatLite[]>({
    fetcher: fetchThreats,
    extract: extractThreats,
    enabled: editable && !!threadId,
  });

  const threatOptions: OptionLite[] = (() => {
    const seen = new Set<string>();
    const out: OptionLite[] = [];
    for (const t of threatsFetch.data ?? []) {
      const id = String(t.threat_id ?? '');
      if (!id || seen.has(id)) continue;
      seen.add(id);
      const desc = t.threat_scenario
        ? String(t.threat_scenario).slice(0, 60) + (String(t.threat_scenario).length > 60 ? '…' : '')
        : '';
      const label = desc ? `${id} — ${desc}` : id;
      out.push({ value: id, label });
    }
    return out;
  })();

  /* ----- CRUD ----- */

  const crud = useRecordCRUD({
    threadId,
    agentName: AGENT_NAME,
    enabled: editable,
  });

  /* ----- Hierarchical approve ----- */

  const hierarchicalEnabled = analysisMode === 'hierarchical';
  const records = (structured.data ?? []) as Array<Record<string, unknown>>;
  const hierApprove = useHierarchicalApprove({
    stepId: 5,
    threadId,
    agentName: AGENT_NAME,
    enabled: hierarchicalEnabled,
    records,
    primaryKey: 'attack_path_id',
    state,
    actions,
    onApproved: structured.refetch,
  });

  return {
    status,
    errorMessage: stepState?.errorMessage ?? null,
    editable,
    structured,
    crud,
    threatOptions,
    assetOptions,
    hierarchical: {
      enabled: hierarchicalEnabled,
      modifications: hierApprove.modifications,
      setRelevance: hierApprove.setRelevance,
      setJustification: hierApprove.setJustification,
      onApprove: hierApprove.onApprove,
      approving: hierApprove.approving,
      approveError: hierApprove.approveError,
    },
    approveOverride: hierarchicalEnabled
      ? { onApprove: hierApprove.onApprove, approving: hierApprove.approving }
      : undefined,
  };
}
